<script setup lang="ts">
import type { CustomerOrder } from '@vue-storefront/prestashop-api';

const { orders } = useOrders();
const { bulkEligibility } = useReturns();
const localePath = useLocalePath();

const userOrders: ComputedRef<CustomerOrder[] | null> = computed(() => orders.value);
const ordersList = computed(() => (userOrders.value?.length ? populateOrders(userOrders.value) : []));
</script>

<template>
  <div class="order-list">
    <div v-if="ordersList.length === 0" class="no-orders">
      <VEmptyState :title="$t('You have no orders yet')" :description="$t('You can find lots of items on our marketplace')" />
    </div>

    <template v-else>
      <!-- sm and up: table -->
      <div class="hidden overflow-hidden rounded-lg border border-gray-200 shadow-sm sm:block">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                {{ $t('Reference') }}
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                {{ $t('Date placed') }}
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                {{ $t('Status') }}
              </th>
              <th scope="col" class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                {{ $t('Total amount') }}
              </th>
              <th scope="col" class="px-6 py-3">
                <span class="sr-only">{{ $t('Actions') }}</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="order in ordersList" :key="order.id" class="hover:bg-gray-50">
              <td class="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                {{ order.reference }}
              </td>
              <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                <time :datetime="order.date">{{ order.date }}</time>
              </td>
              <td class="whitespace-nowrap px-6 py-4 text-sm">
                <div class="flex items-center gap-1.5 text-gray-500">
                  <span class="h-2 w-2 shrink-0 rounded-full" :style="{ backgroundColor: order.statusColor }" />
                  {{ order.status }}
                </div>
                <span
                  v-if="bulkEligibility[Number(order.id)]?.has_active_request"
                  class="mt-1 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800"
                >
                  {{ $t('Return request pending') }}
                </span>
              </td>
              <td class="whitespace-nowrap px-6 py-4 text-right text-sm font-medium text-gray-900">
                {{ order.total }}
              </td>
              <td class="whitespace-nowrap px-6 py-4 text-right text-sm">
                <div class="flex items-center justify-end gap-2">
                  <NuxtLink
                    v-if="bulkEligibility[Number(order.id)]?.return"
                    :to="localePath(`/my-account/orders/${order.id}?action=refund`)"
                    class="btn btn-secondary"
                  >
                    {{ $t('Ask for refund') }}
                  </NuxtLink>
                  <NuxtLink :to="localePath(`/my-account/orders/${order.id}`)" class="btn btn-secondary">
                    {{ $t('View') }}
                  </NuxtLink>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- below sm: stacked cards -->
      <div class="grid grid-cols-1 gap-4 sm:hidden">
        <div v-for="order in ordersList" :key="order.id" class="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div class="flex items-baseline justify-between gap-2 border-b border-gray-200 px-4 py-3 text-sm">
            <span class="font-medium text-gray-900">{{ order.reference }}</span>
            <time :datetime="order.date" class="text-gray-500">{{ order.date }}</time>
          </div>
          <div class="flex flex-col gap-4 p-4">
            <div class="flex items-start justify-between gap-4">
              <div>
                <dt class="text-xs font-medium uppercase tracking-wide text-gray-500">{{ $t('Status') }}</dt>
                <dd class="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
                  <span class="h-2 w-2 shrink-0 rounded-full" :style="{ backgroundColor: order.statusColor }" />
                  {{ order.status }}
                </dd>
                <span
                  v-if="bulkEligibility[Number(order.id)]?.has_active_request"
                  class="mt-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800"
                >
                  {{ $t('Return request pending') }}
                </span>
              </div>
              <div class="text-right">
                <dt class="text-xs font-medium uppercase tracking-wide text-gray-500">{{ $t('Total amount') }}</dt>
                <dd class="mt-1 text-sm font-medium text-gray-900">{{ order.total }}</dd>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <NuxtLink
                v-if="bulkEligibility[Number(order.id)]?.return"
                :to="localePath(`/my-account/orders/${order.id}?action=refund`)"
                class="btn btn-secondary flex-1 text-center"
              >
                {{ $t('Ask for refund') }}
              </NuxtLink>
              <NuxtLink :to="localePath(`/my-account/orders/${order.id}`)" class="btn btn-secondary flex-1 text-center">
                {{ $t('View') }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
